// React and related imports
import React, { useState, useEffect, useId } from 'react';

// Flowbite components
import { Label, Select, TextInput } from 'flowbite-react';

interface TimeDifferenceInputProps {
  id?: string;
  label: string;
  value?: string; // Format: "+<minutes>" or "-<minutes>" (default: "+00")
  onChange: (value: string) => void;
}

export const TimeDifferenceInput: React.FC<TimeDifferenceInputProps> = ({
  id,
  label,
  value = '+00',
  onChange,
}) => {
  const generatedId = useId();
  const effectiveID = id || generatedId;

  const [sign, setSign] = useState(value.startsWith('-') ? '-' : '+');
  const [minutes, setMinutes] = useState(value.replace(/^[+-]/, ''));

  useEffect(() => {
    setSign(value.startsWith('-') ? '-' : '+');
    setMinutes(value.replace(/^[+-]/, ''));
  }, [value]);

  const handleSignChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newSign = event.target.value;
    setSign(newSign);
    onChange(`${newSign}${minutes}`);
  };

  const handleMinutesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMinutes = event.target.value;
    setMinutes(newMinutes);
    onChange(`${sign}${newMinutes}`);
  };

  return (
    <div>
      <div className="mb-2 block">
        <Label htmlFor={effectiveID}>
          {label}
          <span className="ms-1 text-red-500">*</span>
        </Label>
      </div>

      <div className="flex items-center space-x-3 rtl:space-x-reverse">
        {/* Sign Selector */}
        <Select
          id={effectiveID}
          value={sign}
          onChange={handleSignChange}
          required
          className="block w-16 rounded-md border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary-500 dark:focus:ring-primary-500"
        >
          {['-', '+'].map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </Select>

        {/* Minutes Input */}
        <TextInput
          id={`${effectiveID}-minutes`}
          type="number"
          placeholder="00"
          value={minutes}
          onChange={handleMinutesChange}
          className="w-24 border-gray-300 bg-gray-50 text-center focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
      </div>
    </div>
  );
};
